import { useState } from 'react'
import { useNavigate, useParams } from 'react-router'

import type { HistoryEntry, Session } from '@ctcells/contract'
import { PATHS } from '@ctcells/core'
import type { UserRow } from '@ctcells/fixtures'
import { enumLabel, t, type MessageKey } from '@ctcells/i18n'

import { Button } from '../../atoms/Button.tsx'
import { DataTable, type Column } from '../../atoms/DataTable.tsx'
import type { TableSort } from '../../atoms/tableSort.ts'
import { EmptyState } from '../../atoms/EmptyState.tsx'
import { Field } from '../../atoms/Field.tsx'
import { Modal } from '../../atoms/Modal.tsx'
import { PasswordField } from '../../atoms/PasswordField.tsx'
import { PasswordInput } from '../../atoms/PasswordInput.tsx'
import { passwordViolations } from '../../atoms/passwordPolicy.ts'
import { StatusBadge } from '../../atoms/StatusBadge.tsx'
import { userStatusTone } from '../../atoms/statusTone.ts'
import { TextInput } from '../../atoms/TextInput.tsx'
import { useSubmitValidation, type ValidationErrors } from '../../atoms/useSubmitValidation.ts'
import { ValueRows } from '../../atoms/ValueRows.tsx'
import { SettingsRow } from '../../layout/SettingsList.tsx'
import { gate, type ActionGate, type ScreenAccess } from '../access.ts'
import { dateTimeText, orDash } from '../format.ts'
import { Card, DetailPage, OfflineNotice } from '../resourceParts.tsx'

import { ChoiceRow } from './settingsRows.tsx'
import {
  UserSessionPolicyCard,
  type UserSessionPolicyEdit,
  type UserSessionPolicyView,
} from './UserSessionPolicyCard.tsx'

import '../screens.css'
import '../resource.css'

/**
 * 사용자 상세 — 와이어프레임 `settings()` `userDetail` 2712행.
 *
 * 🔴 **관리 › 세션 탭이 여기로 흡수됐다**(§1-4). 세션 강제 종료는 이 화면에서 한다.
 * 🔴 **글자 아바타 금지**(§1-2). 이름 첫 글자 배지를 만들지 마라.
 * 🔴 **조작은 머리 단추 줄이 아니라 「계정 설정」 카드의 줄이다**(eric 2026-09-28 「사용자 --> 설정 컨셉대로..
 *    하나의 row 에다가 배치해줘」). 왼쪽 줄 이름 · 오른쪽 조작 — 관리 › 설정 · 내 계정과 **같은 줄**
 *    (`SettingsRow` · `ChoiceRow`)이다. 머리(`DetailPage.actions`)에 단추를 다시 세우지 마라.
 * 🔴 **역할은 알약 묶음**(`Segmented` — 운영 모드 선택기와 같은 원자)이다. 드롭다운이 아니다
 *    (eric 2026-09-28 「admin 드랍다운 말고.. 이렇게.. 선택하는 걸로 해줘」). 선택지는 **서버 어휘 셋**뿐 —
 *    `service` 는 서버에 없다(`app_user.role CHECK`). 고르면 확인 창을 거쳐 `PUT` 이 나가고,
 *    칠해진 칸은 **서버가 받은 뒤 재조회된 `user.role`** 이다(낙관적 갱신 금지).
 *
 * 🔴 **이 화면은 fixture 를 모른다.** 목 값도 컨테이너(`UserDetailDemo`)가 넘긴다.
 *    예전에는 기본값으로 fixture 를 쥐고 화면 안에서 `USERS` 를 `username` 으로 뒤져 `user_id` 를
 *    얻었는데, 실물에는 그 fixture 가 없어 **live 에서 세션 표·감사 표가 통째로 비었다.**
 *    데이터는 전부 props 로 들어온다 — 화면이 출처를 고르지 않는다.
 *
 * 🔴 **삭제는 되돌릴 수 없어 확인 대화를 한 번 둔다.** `Modal` 주석이 「그 외에는 만들지 마라」로
 *    3곳을 못 박았는데, 되돌릴 수 없는 파괴적 행위가 그 뒤에 생겨 네 번째로 추가했다(eric 지시).
 *    E-STOP 이 확인 없이 즉시인 것은 **안전 정지**라서다 — 삭제는 반대 방향이라 같이 취급하지 않는다.
 * 🔴 확인 대화가 **사유를 받는다.** 취향이 아니라 계약이다 — `DELETE /users/{id}` 본문이
 *    `IrreversibleCommandEnvelope` 이고 `reason` 이 필수(minLength 1)라, 없으면 422 다.
 */
export interface UserDetailScreenProps {
  users: readonly UserRow[]
  sessions: readonly Session[]
  audits: readonly HistoryEntry[]
  access?: ScreenAccess
  /**
   * 역할 변경이 서버에 거절된 사유(에러 코드 사전 문구 + 코드 원문). **역할 줄 오른쪽**에 선다 —
   * 확인 창을 닫아도 남는다. 칠해진 칸은 그대로다(서버 값이 안 바뀌었으니까).
   */
  roleError?: string | undefined
  /**
   * 로그인한 사람의 `user_id`. 자기 계정 삭제를 막는 축이다.
   * 🔴 `undefined`(= 아직 모름)면 막지 않는다 — 집행은 서버다. 화면이 「내가 누구인지」를 추론하지 않는다.
   */
  currentUserId?: string | undefined
  /** 지금 이 브라우저의 세션 id. 내가 앉아 있는 세션을 스스로 끊지 못하게 막는 축이다 */
  currentSessionId?: string | undefined
  /**
   * 만료 판정에 쓰는 «지금»(epoch ms). 🔴 **컨테이너가 렌더 시각을 한 번 넘긴다** — 화면이 `Date.now()` 를
   *    부르지 않는다(시험에서 고정할 수 있게). 주기 재계산 · 폴링 없음.
   * ⚠ 안 넘기면 거르지 않는다 — 시각을 모르면 「지났다」를 말할 근거가 없다.
   */
  now?: number | undefined
  /**
   * 확인까지 끝난 계정 조작. 🔴 **확인 대화를 거친 것만 여기로 온다.**
   * 계약 필수값(`reason` · `newPassword`)은 빈 문자열로 오지 않는다 — 화면이 먼저 막는다.
   * 🔴 낙관적 갱신 금지 — 목록·상태가 바뀌는 것은 서버 응답 뒤다
   */
  onAction?: ((user: UserRow, action: UserAction) => void) | undefined
  /** 요청이 날아가 있는 동안. 확인 버튼을 다시 누르지 못하게 한다 */
  pending?: boolean | undefined
  /** 실패 문구. 컨테이너가 `errorMessage()`(에러 코드 사전)로 만들어 넘긴다 */
  actionError?: string | undefined
  /**
   * 서랍(사용자 목록 오른쪽 판)으로 열렸을 때의 닫기. 없으면 전체 화면이고 「← 사용자」 줄이다.
   * 🔴 **장비 관점 콘솔은 이 화면을 전체 화면으로 계속 쓴다** — 그래서 옵션이다.
   */
  onClose?: (() => void) | undefined
  /**
   * 이 계정의 실효 세션 만료(`GET /users/{id}/session-policy` · `IFC-RO-89`). 🔴 **안 넘기면 카드가 없다** —
   * demo 경로는 넘기지 않는다(값을 지어내지 않는다). 컨테이너가 화면이 고른 계정의 `user_id` 로 부른다.
   */
  sessionPolicy?: UserSessionPolicyView | undefined
  /** 그 카드의 계정 단 넣기 · 지우기(`PUT /settings/session-policy` scope `user:<id>`). 안 넘기면 보기만 */
  sessionPolicyEdit?: UserSessionPolicyEdit | undefined
}

/**
 * 계정 조작 한 건. 🔴 **모양이 계약 본문을 따른다** — 실서버 스펙 대조(2026-09-18).
 *
 * | 조작            | 경로                        | 본문                            |
 * | --------------- | --------------------------- | ------------------------------- |
 * | `deactivate`    | `DELETE /users/{id}`        | `IrreversibleCommandEnvelope`   |
 * | `lock`·`unlock` | `POST /users/{id}/(un)lock` | `CommandEnvelope`               |
 * | `password`      | `POST /users/{id}/password` | `PasswordResetEnvelope`         |
 * | `revokeSession` | `DELETE /sessions/{id}`     | `CommandEnvelope`               |
 *
 * 🔴 **「모든 세션 종료」는 없다**(eric 2026-09-30 「모든 세션 종료 버튼도 없애줘」). 일괄 폐기 경로가 계약에
 *    0건이라 세션 수만큼 `DELETE` 를 순차로 보내던 갈래(원자적이지 않았다)를 통째로 걷었다. 세션은 줄마다
 *    「강제 종료」로만 끊는다 — 되살리지 마라.
 */
export type UserAction =
  /**
   * 계정 비활성(soft-delete · UAM-ACC-05). 🔴 **「삭제」 버튼을 따로 두지 않는다**(eric 확정
   * 2026-09-18 「비활성 이 삭제인거야? 그럼 삭제는 하지 말고 비활성만 놔둬」) — 계약에 경로가
   * 하나뿐이라 버튼이 둘이면 같은 요청을 두 이름으로 부르는 것이 된다.
   * ⚠ **재활성 경로가 계약에 0건이다.** 잠금과 달리 되돌릴 수 없다.
   */
  | { kind: 'deactivate'; reason: string }
  | { kind: 'lock' }
  | { kind: 'unlock' }
  | { kind: 'password'; newPassword: string }
  | { kind: 'revokeSession'; sessionId: string }
  /** `PUT /users/{id}` — 본문은 컨테이너가 받은 `username`·`name` 위에 이 역할만 얹어 짓는다(전체 교체) */
  | { kind: 'role'; role: WritableRole }

/** 서버가 받는 역할 셋(`UserUpdateEnvelope.role` enum). 화면 어휘의 `service` 는 여기 없다 */
const WRITABLE_ROLES = ['viewer', 'operator', 'admin'] as const
export type WritableRole = (typeof WRITABLE_ROLES)[number]

/**
 * 두 표의 처음 정렬 — 시각 최신 위(eric 2026-09-30 「모든 게시판」 머리글 정렬).
 * 세션은 서버 차례(`GET /sessions` = `issued_at desc` · `app/views/sessions.py`)와 같고, 감사는 `/history`(`ts desc`)와 같다.
 */
const SESSIONS_DEFAULT_SORT: TableSort = { key: 'issued', direction: 'desc' }
const AUDIT_DEFAULT_SORT: TableSort = { key: 'ts', direction: 'desc' }

/** 감사 행위 표기 — 칸과 머리글 정렬이 같은 글자를 쓴다 */
function auditActionLabel(entry: HistoryEntry): string {
  const action = entry.detail?.['action']
  return typeof action === 'string' ? enumLabel('enum.auditAction', action) : t('mask.none')
}

function isWritableRole(role: string): role is WritableRole {
  return (WRITABLE_ROLES as readonly string[]).includes(role)
}

/** 확인 대화가 지금 무엇을 물어보고 있는가. `null` 이면 닫혀 있다 */
type Pending =
  | { kind: 'deactivate' | 'lock' | 'unlock' | 'password' }
  | { kind: 'revokeSession'; sessionId: string }
  | { kind: 'role'; role: WritableRole }

const CONFIRM_TITLE: Record<Pending['kind'], MessageKey> = {
  deactivate: 'screen.userDeactivate.title',
  lock: 'screen.userLock.title',
  unlock: 'screen.userUnlock.title',
  password: 'screen.userPassword.title',
  revokeSession: 'screen.sessionRevoke.title',
  role: 'screen.userRole.title',
}

/** 확인 버튼 라벨 — 되돌릴 수 없는 것은 그 동작 이름을 그대로 쓴다(「확인」으로 뭉개지 않는다) */
const CONFIRM_LABEL: Record<Pending['kind'], MessageKey> = {
  deactivate: 'action.deactivate',
  lock: 'action.lock',
  unlock: 'action.unlock',
  password: 'action.resetPassword',
  revokeSession: 'action.endSession',
  role: 'action.changeRole',
}

export function UserDetailScreen({
  users,
  sessions,
  audits,
  access = {},
  roleError,
  currentUserId,
  currentSessionId,
  now,
  onAction,
  pending,
  actionError,
  onClose,
  sessionPolicy,
  sessionPolicyEdit,
}: UserDetailScreenProps) {
  const { userId } = useParams()
  const navigate = useNavigate()
  const [confirm, setConfirm] = useState<Pending | null>(null)
  const [reason, setReason] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  /**
   * 보낸 확인 대화가 어디까지 갔는가. 🔴 **서버가 받은 뒤에만 창을 닫는다** — 실패면 사유를 창 안에
   * 세운 채 둔다. `sent`(눌렀다) → `inflight`(요청이 떴다) → 떨어졌을 때 실패가 없으면 닫는다.
   * ⚠ 누른 직후에는 `pending` 이 아직 안 켜졌을 수 있어 두 단계로 본다(바로 닫으면 실패를 못 본다).
   */
  const [phase, setPhase] = useState<'idle' | 'sent' | 'inflight'>('idle')
  /** 이 창에서 한 번이라도 보냈는가 — 실패 문구는 그 뒤에만 선다 */
  const [submitted, setSubmitted] = useState(false)
  /*
   * 🔴 사유 · 새 비밀번호 · 확인 칸은 **누를 때 잰다**(eric 2026-10-27). 실행 버튼은 권한 · 상태 · 전송 중만 막는다
   *    (`confirmGate`) — 빈 칸은 그 칸 아래 안내로 말한다
   */
  const confirmForm = useSubmitValidation(validateConfirm)
  const reasonField = confirmForm.field('reason')
  const newPasswordField = confirmForm.field('newPassword')
  const confirmField = confirmForm.field('confirm')

  /*
   * `pending` 이 바뀐 그 그리기에서 단계를 옮긴다(React 「렌더 중 상태 조정」 — 효과 안 `setState` 를 쓰지 않는다).
   */
  const [seenPending, setSeenPending] = useState(pending === true)
  if ((pending === true) !== seenPending) {
    setSeenPending(pending === true)
    if (phase === 'sent' && pending === true) setPhase('inflight')
    if (phase === 'inflight' && pending !== true) {
      setPhase('idle')
      if (actionError === undefined) setConfirm(null)
    }
  }
  const user = users.find((u) => u.username === userId)
  /**
   * 서랍(사용자 목록 오른쪽 판)으로 열렸는가. 🔴 **`onClose` 가 그 축이다** — 서랍만 닫기를 넘긴다.
   * 폭이 좁아 모양이 갈리는 곳이 여기 하나뿐이라 값을 따로 받지 않는다(prop 을 늘리지 않는다).
   */
  const drawer = onClose !== undefined

  if (!user) {
    return (
      <DetailPage
        backLabel={t('tab.admin.members')}
        backTo={PATHS.admin}
        onClose={onClose}
        title={t('screen.userDetail.title')}
      >
        <EmptyState label={t('state.empty.users')} />
      </DetailPage>
    )
  }

  /*
   * 🔴 **만료 세션은 표에서 뺀다 — 프론트가 시계로 판정한다.** 「서버가 판정한다」(DP-04)의 **명시 예외**다.
   *    eric 원문(2026-09-30): 「세션 지난 거랑.. 비교해서... 프론트에서 현재 시간과 비교해서 지난 건 버리면 되잖아」.
   *    까닭: `GET /sessions` 는 `revoked=false` 만 거르고 `expires_at` 이 지난 세션도 그대로 준다
   *    (실측 2026-09-30 — 113건 중 108건이 시각상 지났고 전부 `revoked:false`). 응답에 만료 판정 칸이 없다.
   *    ⇒ **서버가 만료분을 빼 주거나 판정 칸을 실으면 이 비교를 걷고 그쪽으로 되돌린다.**
   * 🔴 `revoked` 는 서버 판정값이라 그대로 거른다.
   * 🔴 **지금 쓰는 세션(`currentSessionId`)은 시각과 무관하게 남긴다.** 서버가 방금 이 요청을 받아 준 세션이다 —
   *    `expires_at` 은 idle 갱신형(`services/auth.py touch_session` 이 `last_seen_at` 과 같이 민다)이라
   *    갱신 간격(`SESSION_TOUCH_INTERVAL_SEC`) 사이에는 받아 둔 값이 지나 보일 수 있다.
   * ⚠ `expires_at` 을 못 읽으면(NaN) 거르지 않는다 — 모르는 것을 「지났다」로 접지 않는다.
   * 뺀 건수는 표 아래 한 줄로 적는다 — 「못 봤다」가 「없다」로 읽히지 않게.
   */
  const liveSessions = sessions.filter((s) => s.user_id === user.user_id && !s.revoked)
  const isExpired = (s: Session): boolean =>
    now !== undefined && s.session_id !== currentSessionId && Date.parse(s.expires_at) < now
  const mySessions = liveSessions.filter((s) => !isExpired(s))
  const expiredHidden = liveSessions.length - mySessions.length
  const myAudits = audits.filter((entry) => matchesUser(entry, user))
  const settingsGate = gate(access, { perm: 'settings' })
  const locked = user.status === 'locked'

  /**
   * 🔴 자기 계정에는 못 하는 것들. 사유 우선순위는 `gate()` 가 정한다 —
   *    연결 끊김 · 권한 없음이 먼저고, 둘 다 통과했을 때만 「자기 계정」이 사유로 남는다.
   * 🔴 `currentUserId` 가 `undefined`(아직 모름)면 막지 않는다. 집행은 서버다.
   */
  const isSelf = currentUserId !== undefined && currentUserId === user.user_id
  const selfSafe = (stateReason: MessageKey): ActionGate =>
    gate(access, { perm: 'settings', stateOk: !isSelf, stateReason })

  const deactivateGate = selfSafe('reason.selfDeactivate')
  // 자기 계정을 잠그면 스스로 못 들어온다
  const lockGate = locked ? settingsGate : selfSafe('reason.selfLock')
  /*
   * 🔴 본인 재설정은 계약이 `current_password` 를 요구한다(`PasswordResetEnvelope` · UAM-PWD-02).
   *    이 대화는 관리자 강제 재설정이라 그 칸이 없다 — 자기 계정에 쓰면 422 다. 그래서 막는다.
   */
  const passwordGate = selfSafe('reason.selfPassword')
  /*
   * 역할 — 🔴 **자기 계정도 막지 않는다.** 서버(`views/users.py` `update_user`)는 자기 역할 변경을
   *    따로 막지 않고, 막는 것은 «마지막 활성 관리자의 강등»(409 `LAST_ADMIN_PROTECT`) 하나다.
   *    그 판정은 활성 admin 수를 세야 해서 화면이 미리 하지 않는다 — 거절 사유가 이 줄에 선다.
   */
  const roleGate = settingsGate
  /** ⚠ 재활성 경로가 계약에 0건이다 — 비활성 계정의 「활성」은 늘 비활성 + 사유 */
  const disabledAccount = user.status === 'disabled'
  const statusGate = disabledAccount
    ? gate(access, { perm: 'settings', stateOk: false, stateReason: 'reason.noReactivateRoute' })
    : deactivateGate
  /** 세션 한 줄의 강제 종료. 내가 지금 앉아 있는 세션은 스스로 끊지 않는다 */
  const sessionGate = (session: Session): ActionGate =>
    gate(access, {
      perm: 'settings',
      stateOk: !(currentSessionId !== undefined && currentSessionId === session.session_id),
      stateReason: 'reason.selfSession',
    })

  /** 지금 열린 확인 대화의 입력 — 누를 때 `validateConfirm` 이 잰다 */
  const confirmDraft = (kind: Pending['kind']): ConfirmDraft => ({
    kind,
    reason,
    newPassword,
    confirmPassword,
  })

  /**
   * 확인 대화의 실행 버튼 — 권한 · 그 조작의 상태(`base`) · 전송 중만 막는다.
   * 요청이 떠 있는 동안 막는다 — 멱등키는 요청마다 새로 나므로 중복 발사가 중복 «명령»이 된다.
   */
  const confirmGate = (base: ActionGate): ActionGate => {
    if (pending === true) return { disabled: true, disabledReason: t('reason.actionInFlight') }
    return gate(access, {
      perm: 'settings',
      stateOk: base.disabled === false,
      stateReason: 'reason.commandNotAllowed',
    })
  }

  /** 확인 대화를 연다. 이전에 친 값이 남아 넘어가지 않게 매번 비운다 */
  const open = (next: Pending) => {
    setReason('')
    setNewPassword('')
    setConfirmPassword('')
    setPhase('idle')
    setSubmitted(false)
    confirmForm.reset()
    setConfirm(next)
  }

  /** 그 조작을 애초에 할 수 있었는가 — 대화를 연 버튼과 같은 판정을 확인 버튼에도 건다 */
  const baseGate = (next: Pending): ActionGate =>
    next.kind === 'deactivate'
      ? deactivateGate
      : next.kind === 'lock'
        ? lockGate
        : next.kind === 'password'
          ? passwordGate
          : next.kind === 'role'
            ? roleGate
            : settingsGate

  /** 확인된 조작을 계약 본문 모양으로 만든다. 🔴 필수값은 여기서 이미 채워져 있다 */
  const actionOf = (next: Pending): UserAction => {
    switch (next.kind) {
      case 'deactivate':
        return { kind: 'deactivate', reason: reason.trim() }
      case 'password':
        return { kind: 'password', newPassword }
      case 'revokeSession':
        return { kind: 'revokeSession', sessionId: next.sessionId }
      case 'role':
        return { kind: 'role', role: next.role }
      default:
        return { kind: next.kind }
    }
  }

  /*
   * 역할 선택지 — 서버 어휘 셋. 🔴 **지금 역할이 셋 밖이면(`service` · 목 데이터) 그것도 앞에 보인다** —
   *    안 그리면 아무 칸도 안 칠해져 「역할이 없다」로 읽힌다. 그 칸은 골라도 나가지 않는다.
   */
  const roleOptions: { value: string; label: string }[] = [
    ...(isWritableRole(user.role) ? [] : [{ value: user.role, label: user.role }]),
    ...WRITABLE_ROLES.map((role) => ({ value: role, label: role })),
  ]

  /**
   * 「계정 설정」 카드 — 한 줄에 조작 하나(eric 2026-09-28). 줄 모양은 관리 › 설정 · 내 계정의 그것이다.
   * 🔴 단추 글자는 지금 상태를 따라 바뀐다(잠금 ↔ 잠금 해제 · 비활성 ↔ 활성). 확인 창·요청은 종전 그대로다.
   */
  const settingsCard = (
    <Card title={t('card.accountSettings')}>
      <ChoiceRow
        baseGate={roleGate}
        io={{
          pending: pending === true,
          error: roleError,
          onApply: (next: string) => {
            // 지금 역할과 같거나 서버가 모르는 값이면 창을 열지 않는다
            if (next === user.role || !isWritableRole(next)) return
            open({ kind: 'role', role: next })
          },
        }}
        label={t('column.role')}
        options={roleOptions}
        value={user.role}
      />
      <SettingsRow
        action={
          <Button onClick={() => open({ kind: 'password' })} size="pill" {...passwordGate}>
            {t('action.reset')}
          </Button>
        }
        label={t('field.password')}
        value={null}
      />
      <SettingsRow
        action={
          <Button
            onClick={() => open({ kind: locked ? 'unlock' : 'lock' })}
            size="pill"
            {...lockGate}
          >
            {locked ? t('action.unlock') : t('action.lock')}
          </Button>
        }
        label={t('action.lock')}
        value={null}
      />
      {/*
        🔴 「삭제」 단추는 두지 않는다. 계약 경로가 `DELETE /users/{id}` 하나뿐이고 그게
           **soft-delete(= 비활성)** 라, 단추가 둘이면 같은 요청을 두 이름으로 부르게 된다
           (eric 확정 2026-09-18). 되돌릴 수 없는 연산이라 `variant="danger"`(붉은 테)를 유지한다.
      */}
      <SettingsRow
        action={
          disabledAccount ? (
            <Button size="pill" {...statusGate}>
              {t('action.activate')}
            </Button>
          ) : (
            <Button
              onClick={() => open({ kind: 'deactivate' })}
              size="pill"
              variant="danger"
              {...statusGate}
            >
              {t('action.deactivate')}
            </Button>
          )
        }
        label={t('row.accountStatus')}
        value={null}
      />
    </Card>
  )

  /*
   * 🔴 **「세션 만료」 열은 없다**(eric 2026-09-30). 값(`Session.expires_at`)이 지난 줄도 서버가 그대로 줘서
   *    「만료」라 적힌 세션이 목록에 서 있었다. 남는 열: 기기 · 로그인 · 마지막 활동 · 강제 종료.
   * 폭은 사용자 표와 같은 방식이다 — 전부 `fit` 이라 남는 폭을 똑같이 나눠 열 사이가 고르다.
   */
  const sessionColumns: Column<Session>[] = [
    /*
     * 🔴 **`orDash` 를 거친다** — `Session.client` 는 서버가 `exclude_none=True` 로 내보내
     *    **키째 빠져서 온다**(실물 24건 중 8건이 그랬다). 그대로 그리면 React 가 `undefined` 를
     *    아무것도 안 그려서 **빈 칸**이 되고, 「모른다」와 「접속 기기가 없다」가 구별되지 않는다.
     */
    {
      key: 'client',
      header: t('column.device'),
      width: 'fit',
      sort: 'text',
      sortValue: (s) => s.client,
      cell: (s) => orDash(s.client),
    },
    {
      key: 'issued',
      header: t('column.loggedInAt'),
      width: 'fit',
      sort: 'time',
      sortValue: (s) => s.issued_at,
      cell: (s) => <span className="muted">{dateTimeText(s.issued_at)}</span>,
    },
    {
      key: 'lastSeen',
      header: t('column.lastActive'),
      width: 'fit',
      sort: 'time',
      sortValue: (s) => s.last_seen_at,
      cell: (s) => dateTimeText(s.last_seen_at),
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      width: 'fit',
      cell: (session) => (
        <Button
          onClick={() => open({ kind: 'revokeSession', sessionId: session.session_id })}
          size="pill"
          {...sessionGate(session)}
        >
          {t('action.endSession')}
        </Button>
      ),
    },
  ]

  /**
   * 활성 세션 카드 — 서랍에서는 **혼자** 서고, 전체 화면에서는 「계정」 카드와 2열로 선다.
   * 🔴 한 벌만 만들어 두 자리가 나눠 쓴다. 복붙하면 한쪽만 고쳐져 어긋난다.
   */
  const sessionsCard = (
    <Card title={t('card.activeSessions')}>
      <DataTable
        caption={t('card.activeSessions')}
        columns={sessionColumns}
        defaultSort={SESSIONS_DEFAULT_SORT}
        density="compact"
        emptyLabel={t('state.empty.sessions')}
        rowKey={(s) => s.session_id}
        rows={mySessions}
      />
      {expiredHidden > 0 ? (
        <p className="muted">{t('state.sessionsExpiredHidden', { count: expiredHidden })}</p>
      ) : null}
    </Card>
  )

  /**
   * 머리의 메타 줄. 🔴 **서랍일 때만 잠금 해제 시각이 붙는다** — 「계정」 카드가 빠지면서
   * 그 한 칸만 갈 곳이 없어졌기 때문이다(행에도 없는 값이다). 전체 화면은 카드가 그대로 있어
   * 여기 또 적으면 같은 값이 두 번 선다.
   * ⚠ 「잠김」 자체는 머리 배지가 이미 말한다 — 여기 적는 것은 **해제 시각**뿐이다.
   */
  const submeta = [
    user.name,
    user.role,
    // 🔴 값은 `User.last_login_at` 이다 — 이름을 붙여 「마지막 활동」으로 읽히지 않게 한다(eric 2026-09-30)
    `${t('row.lastLogin')} ${user.last_seen_text}`,
    ...(drawer && user.locked_until != null
      ? [`${t('action.unlock')} ${dateTimeText(user.locked_until)}`]
      : []),
  ].join(' · ')

  return (
    <DetailPage
      backLabel={t('tab.admin.members')}
      backTo={PATHS.admin}
      onClose={onClose}
      badge={
        <StatusBadge
          /* 테 없는 알약 — 작업 준비 · 알람 심각도 칩과 같은 규칙(eric 2026-10-30 「관리 › 사용자 쪽도 테두리 없게」) · 색 · 글자 그대로 */
          flat
          label={enumLabel('enum.userStatus', user.status)}
          status={user.status === 'active' ? 'IDLE' : 'STOPPED'}
          /* 시안 알약 색 — 활성 초록 · 잠김 호박 · 비활성 중성(eric 2026-10-12 「사용자에서 상태.. 디자인팀의 색상으로」) */
          tone={userStatusTone(user.status)}
        />
      }
      submeta={submeta}
      title={user.username}
    >
      <OfflineNotice shown={access.offline === true} />
      {/*
        🔴 **서랍에서는 「계정」 카드가 없다**(eric 2026-09-23 「이건 row 에 보이는 거니 상세에선
           다 빼」). 목록 행이 계정 ID · 이름 · 역할 · 상태 · 마지막 로그인을 이미 말하고 있어서
           그 옆 판이 같은 값을 한 번 더 읽어 준다 — 서랍은 «행에 없는 것»만 말한다.
           행에 없던 것은 잠금 해제 시각 하나뿐이고 그건 머리 메타 줄로 올렸다(`submeta`).
        🔴 **전체 화면은 그대로다** — 장비 관점 콘솔은 옆에 목록이 없어 다 보여야 한다.
           그래서 갈리는 축이 `onClose`(= 서랍인가) 하나다. 시험이 양쪽을 못 박는다.
      */}
      {settingsCard}
      {/* 세션 만료 — 「계정 설정」 바로 아래(무엇이 걸려 있나 → 그 계정의 세션 순). 서랍 · 전체 화면 같은 자리 */}
      {sessionPolicy === undefined ? null : (
        <UserSessionPolicyCard edit={sessionPolicyEdit} view={sessionPolicy} />
      )}
      {drawer ? (
        sessionsCard
      ) : (
        <div className="cards cards--2">
          <Card title={t('card.account')}>
            <ValueRows
              rows={[
                { key: 'id', label: t('field.accountId'), value: user.username },
                // 🔴 「이메일」이었다 — `User` 에 그 필드가 없다(2026-09-20). `name` 은 실재한다
                { key: 'name', label: t('column.name'), value: user.name },
                { key: 'role', label: t('column.role'), value: user.role },
                {
                  key: 'status',
                  label: t('column.status'),
                  value: enumLabel('enum.userStatus', user.status),
                },
                {
                  key: 'lastLogin',
                  label: t('row.lastLogin'),
                  value: dateTimeText(user.last_login_at),
                },
                {
                  // 🔴 `status` 만 보면 안 된다. 해제는 경과 뒤 첫 로그인 시도에 일어난다
                  key: 'lockedUntil',
                  label: t('action.lock'),
                  value: orDash(user.locked_until == null ? null : dateTimeText(user.locked_until)),
                },
              ]}
            />
          </Card>

          {sessionsCard}
        </div>
      )}

      <Card title={t('card.recentAudit')}>
        <DataTable
          caption={t('card.recentAudit')}
          columns={[
            {
              key: 'ts',
              header: t('column.time'),
              nowrap: true,
              sort: 'time',
              sortValue: (entry) => entry.ts,
              cell: (entry) => <span className="muted mono">{dateTimeText(entry.ts)}</span>,
            },
            {
              key: 'actor',
              header: t('column.actor'),
              nowrap: true,
              sort: 'text',
              sortValue: (entry) => entry.actor,
              cell: (entry) => orDash(entry.actor),
            },
            {
              key: 'action',
              header: t('column.action'),
              nowrap: true,
              sort: 'text',
              sortValue: auditActionLabel,
              cell: auditActionLabel,
            },
            {
              key: 'target',
              header: t('column.target'),
              nowrap: true,
              sort: 'text',
              sortValue: (entry) => entry.target,
              cell: (entry) => orDash(entry.target),
            },
          ]}
          defaultSort={AUDIT_DEFAULT_SORT}
          density="compact"
          emptyLabel={t('state.empty.audit')}
          rowKey={(entry) => `${entry.ts}-${String(entry.target)}`}
          rows={myAudits}
        />
        <div className="btnrow btnrow--end">
          <Button onClick={() => void navigate(PATHS.logs)} size="pill">
            {t('action.openAuditLog')}
          </Button>
        </div>
      </Card>

      {confirm === null ? null : (
        <Modal
          size="sm"
          footer={
            <>
              <Button onClick={() => setConfirm(null)} size="pill">
                {t('action.cancel')}
              </Button>
              <Button
                size="pill"
                variant={confirm.kind === 'deactivate' ? 'danger' : 'primary'}
                {...confirmGate(baseGate(confirm))}
                onClick={() => {
                  // 바닥 줄은 본문 밖이라 포커스 범위는 문서다 — 칸 id 는 이 창에만 있다
                  if (!confirmForm.validate(confirmDraft(confirm.kind), document.body)) return
                  setSubmitted(true)
                  setPhase('sent')
                  onAction?.(user, actionOf(confirm))
                }}
              >
                {t(CONFIRM_LABEL[confirm.kind])}
              </Button>
            </>
          }
          onClose={() => setConfirm(null)}
          title={t(CONFIRM_TITLE[confirm.kind])}
        >
          {/*
            🔴 설명 문장을 달지 않는다(§1-1). 대상과 성질을 **값으로만** 보인다.
            ⚠ 「되돌리기 · 불가」는 비활성에만 붙는다 — 잠금은 되돌릴 수 있고 둘이 같은 화면에 있어서
              무엇이 영구적인지 구분이 서야 한다. 계약에 있는 사실만 적는다(재활성 경로 0건).
          */}
          <ValueRows
            rows={[
              { key: 'id', label: t('field.accountId'), value: user.username },
              ...(confirm.kind === 'role'
                ? [
                    {
                      key: 'role',
                      label: t('column.role'),
                      value: `${user.role} → ${confirm.role}`,
                    },
                  ]
                : []),
              ...(confirm.kind === 'deactivate'
                ? [
                    {
                      key: 'reversible',
                      label: t('row.reversible'),
                      value: t('value.irreversible'),
                    },
                  ]
                : []),
            ]}
          />
          {/* 계약 필수값(`IrreversibleCommandEnvelope.reason`). 감사 원장에 그대로 봉인된다 */}
          {confirm.kind === 'deactivate' ? (
            <Field label={t('field.reason')} {...reasonField.fieldProps}>
              <TextInput
                {...reasonField.inputProps}
                onChange={(value) => {
                  setReason(value)
                  confirmForm.change('reason', { ...confirmDraft(confirm.kind), reason: value })
                }}
                value={reason}
                wide
                {...deactivateGate}
              />
            </Field>
          ) : null}
          {/* 계약 필수값(`PasswordResetEnvelope.new_password`). 정책 위반은 필드가 스스로 알린다 */}
          {confirm.kind === 'password' ? (
            <>
              <PasswordField
                error={newPasswordField.fieldProps.error}
                id={newPasswordField.inputProps.id}
                label={t('field.newPassword')}
                onChange={(value) => {
                  setNewPassword(value)
                  confirmForm.change(
                    'newPassword',
                    { ...confirmDraft(confirm.kind), newPassword: value },
                    ['confirm'],
                  )
                }}
                validation="submit"
                value={newPassword}
              />
              {/*
                🔴 확인 칸 — 내 계정 변경 창과 같은 판정이다(누를 때 `validateConfirm`).
                   이 값은 **보내지 않는다**(`PasswordResetEnvelope` 에 그 칸이 없다).
              */}
              <Field label={t('field.newPasswordConfirm')} {...confirmField.fieldProps}>
                <PasswordInput
                  {...confirmField.inputProps}
                  autoComplete="new-password"
                  onChange={(value) => {
                    setConfirmPassword(value)
                    confirmForm.change('confirm', {
                      ...confirmDraft(confirm.kind),
                      confirmPassword: value,
                    })
                  }}
                  value={confirmPassword}
                  wide
                />
              </Field>
            </>
          ) : null}
          {/* 🔴 이 창에서 보낸 요청의 실패만 적는다 — 앞서 다른 조작이 남긴 사유를 새 창에 옮기지 않는다 */}
          {actionError === undefined || !submitted ? null : (
            <p className="vissue" role="alert">
              {actionError}
            </p>
          )}
        </Modal>
      )}
    </DetailPage>
  )
}

type ConfirmDraft = {
  kind: Pending['kind']
  reason: string
  newPassword: string
  confirmPassword: string
}

/**
 * 확인 대화의 입력 규칙 — 누를 때 잰다. 비활성은 사유(`IrreversibleCommandEnvelope.reason`), 비밀번호 초기화는
 * 새 비밀번호(정책) + 확인 일치. 🔴 확인 칸은 서버로 가지 않는다(`actionOf` 가 새 비밀번호만 싣는다)
 */
function validateConfirm(d: ConfirmDraft): ValidationErrors<'reason' | 'newPassword' | 'confirm'> {
  if (d.kind === 'deactivate')
    return { reason: d.reason.trim() === '' ? t('validation.reasonRequired') : undefined }
  if (d.kind !== 'password') return {}
  const violations = passwordViolations(d.newPassword)
  return {
    newPassword:
      d.newPassword === ''
        ? t('validation.password.newRequired')
        : violations.length > 0
          ? violations.join(' · ')
          : undefined,
    confirm:
      d.confirmPassword === ''
        ? t('validation.password.confirmRequired')
        : d.confirmPassword !== d.newPassword
          ? t('validation.password.mismatch')
          : undefined,
  }
}

/**
 * 감사 한 줄이 이 사용자 것인가.
 *
 * ⚠ **원장이 사람을 두 가지 이름으로 부른다.** 실물 `history.actor` 는 **user_id(UUID)** 이고
 *   목 데이터는 `username` 이다(2026-09-17 실측 · `docs/api-survey.md` 10.8).
 *   → 두 축을 **둘 다** 받는다. 어느 쪽으로 오든 같은 사람의 행이 걸린다.
 *
 * 🔴 **UUID 를 이름으로 바꾸려고 사용자 목록을 뒤지지 않는다.** 표시는 받은 값 그대로다 —
 *    원장이 사람 이름을 주는 것은 서버가 할 일이다(보고 대상).
 */
function matchesUser(entry: HistoryEntry, user: UserRow): boolean {
  const keys = [user.user_id, user.username]
  if (entry.actor != null && keys.includes(entry.actor)) return true
  const target = entry.target ?? ''
  return keys.some((key) => target.endsWith(`:${key}`))
}
